<template>
  <el-dialog
    :visible.sync="visible"
    title="选择租赁方案"
    width="1080px"
    :before-close="handleClose"
    :append-to-body="true"
    :destroy-on-close="true"
    @open="handleOpen"
  >
    <div class="dialog-content">
      <el-alert
        title="最多可选6个租车方案，暂不支持其他类型。商城侧展示租金金额时会换算为日均租金。"
        type="info"
        :closable="false"
      >
      </el-alert>
      <div class="search-area">
        <div class="search-form">
          <div class="form-item">
            <span class="label">租赁方案</span>
            <QS-select
              v-model="rentPeriodType"
              :optionData="rentPeriodTypeOptions"
              :optionConfig="{
                valueKey: 'value',
                labelKey: 'label',
              }"
              filterable
              placeholder="请选择"
            />
            <el-button type="primary" class="search-btn" @click="handleSearch"
              >查询</el-button
            >
          </div>
        </div>
        <div class="add-plan">
          <span class="text">没有适合的租赁方案，</span>
          <el-button type="text" class="action" @click="handleAddPlan"
            >点击新建</el-button
          >
        </div>
      </div>

      <QSCommonTable
        ref="multipleTableRef"
        :loading="loading"
        :data="tableData"
        :columns="colList"
        :border="true"
        row-key="id"
        :pagination="pagination"
        @select="handleSelect"
        @change="handleTableChange"
      />
    </div>
    <template #footer>
      <div class="footer">
        <div>
          已选:
          <el-popover
            placement="bottom"
            width="300"
            trigger="click"
            popper-class="selected-plans-popover"
          >
            <template #reference>
              <span class="a-hover-link">{{ selectedPlans.length }}项</span>
            </template>
            <div class="selected-plans-list">
              <template v-if="selectedPlans.length">
                <div
                  v-for="item in selectedPlans"
                  :key="item.id"
                  class="selected-plan-item"
                >
                  <span class="plan-name">{{ item.name }}</span>
                  <el-button
                    type="text"
                    class="delete-btn"
                    @click="handleDeleteSelected(item)"
                  >
                    删除
                  </el-button>
                </div>
              </template>
              <div v-else class="empty-text">暂无选中项</div>
            </div>
          </el-popover>
        </div>
        <div>
          <el-button @click="handleClose">取消</el-button>
          <el-button
            type="primary"
            @click="handleConfirm"
            :disabled="!confirmBtnDisabled"
            >确定</el-button
          >
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'
import { Tooltip } from '@qs/ui'
import { use$to } from '@/utils/useUtils'
import WindowCommunication from '@/utils/windowCommunication'
import { getPlanListByGoods } from '@/api/domain/shop'

const props = defineProps({
  cityCode: {
    type: String,
    default: '',
  },
  modelId: {
    type: [Number, String],
    default: 0,
  },
  brandId: {
    type: [Number, String],
    default: 0,
  },
  seriesId: {
    type: [Number, String],
    default: 0,
  },
})

const $to = use$to()
const emits = defineEmits(['confirm'])
const communication = ref(null)

const MAX_PLAN_COUNT = 6

const multipleTableRef = ref(null)
// 租赁周期时间单位 1日/2周/3月
const rentPeriodTypeOptions = [
  {
    label: '日租',
    value: 1,
  },
  {
    label: '月租',
    value: 3,
  },
]

const selectedPlans = ref([])
const selectedPlanIds = computed(() => {
  return selectedPlans.value.map((item) => item.id)
})
const confirmBtnDisabled = computed(() => {
  return selectedPlanIds.value.length > 0
})
// 表格列配置
const colList = computed(() => {
  return [
    {
      name: 'selection',
      columnProps: {
        type: 'selection',
        prop: 'selection',
        fixed: 'left',
        selectable: (row) =>
          !(
            selectedPlanIds.value.length >= MAX_PLAN_COUNT &&
            !selectedPlanIds.value.includes(row.id)
          ),
      },
    },
    {
      name: 'id',
      columnProps: {
        prop: 'id',
        label: '租赁方案ID',
      },
    },
    {
      name: 'name',
      columnProps: {
        prop: 'name',
        label: '租赁方案名称',
      },
    },
    {
      name: 'rentPeriod',
      columnProps: {
        prop: 'rentPeriod',
        label: '租期',
      },
      render: ({ row }) => {
        const { rentPeriod, rentPeriodType, rentPeriodTypeName } = row
        if (!rentPeriod) return '--'
        if (rentPeriodType === 1) {
          return `${rentPeriod}天`
        }
        return `${rentPeriod}个${rentPeriodTypeName}`
      },
    },
    {
      name: 'deposit',
      columnProps: {
        prop: 'deposit',
        label: '押金总额',
      },
    },
    {
      name: 'dailyRentAmount',
      columnProps: {
        prop: 'dailyRentAmount',
        label: '日均租金',
      },
      render: ({ row }) => {
        const { dailyRentAmount } = row
        if (!dailyRentAmount) return '--'
        return `${dailyRentAmount}元`
      },
    },
    {
      name: 'otherFeeList',
      columnProps: {
        prop: 'otherFeeList',
        label: '其他费项',
      },
      render: ({ row }) => {
        if (!row.otherFeeList || row.otherFeeList?.length === 0) return '--'
        if (row.otherFeeList?.length === 1) {
          return row.otherFeeList?.[0]
        }
        return (
          <div>
            <span>{row.otherFeeList?.[0]}</span>
            <Tooltip
              effect="dark"
              content={row.otherFeeList?.join('、')}
              placement="top"
            >
              <span class="a-hover-link">等{row.otherFeeList?.length}项</span>
            </Tooltip>
          </div>
        )
      },
    },
    {
      name: 'presentDays',
      columnProps: {
        prop: 'presentDays',
        label: '赠送天数',
      },
      render: ({ row }) => `${row.presentDays}天`,
    },
  ]
})

const pagination = ref({
  pageNum: 1,
  pageSize: 10,
  total: 10,
})
const visible = ref(false)
const loading = ref(false)
const rentPeriodType = ref(3)
const tableData = ref([])

const handleTableChange = ({ pagination: pager }) => {
  pagination.value.pageSize = pager.pageSize
  pagination.value.pageNum = pager.pageNum
  getTableData()
}

const handleClose = () => {
  rentPeriodType.value = 3
  selectedPlans.value = []
  tableData.value = []
  visible.value = false
}

const handleSearch = () => {
  pagination.value.pageNum = 1
  tableData.value = []
  selectedPlans.value = []
  getTableData()
}

const handleSelect = (_, row) => {
  const planIndex = selectedPlans.value.findIndex((item) => item.id === row.id)
  if (planIndex !== -1) {
    selectedPlans.value.splice(planIndex, 1)
    return
  }
  selectedPlans.value.push(row)
}

const getTableData = async () => {
  if (!rentPeriodType.value) return
  loading.value = true
  const [err, res] = await $to(
    getPlanListByGoods({
      pageNum: pagination.value.pageNum,
      pageSize: pagination.value.pageSize,
      rentPeriodType: rentPeriodType.value,
      cityCode: props.cityCode,
      modelId: props.modelId,
    }),
  )
  loading.value = false
  if (err || res?.code !== 1) return
  const { items = [], totalNum = 0 } = res.data
  tableData.value = items
  pagination.value = {
    ...pagination.value,
    total: totalNum,
  }
  await nextTick()
  tableData.value.forEach((row) => {
    if (selectedPlanIds.value.includes(row.id)) {
      multipleTableRef.value.$refs.table.toggleRowSelection(row, true)
    }
  })
}
const handleConfirm = () => {
  if (!selectedPlans.value.length) return
  emits('confirm', selectedPlans.value)
  handleClose()
}

const handleOpen = async () => {
  await getTableData()
}

defineExpose({
  open: () => {
    visible.value = true
  },
  close: () => {
    visible.value = false
  },
})

const handleAddPlan = () => {
  const url = `/car_to_lease/leaseScheme/add?type=1&modelId=${props.modelId}&cityCode=${props.cityCode}&scene=1&brandId=${props.brandId}&seriesId=${props.seriesId}`
  const newWindow = window.open(url, '_blank')
  const currentWindow = window

  if (newWindow) {
    newWindow.addEventListener('beforeunload', () => {
      setTimeout(() => {
        currentWindow.focus()
      }, 100)
    })
  }
}


// 处理方案添加消息
const handleSchemeAdded = (data) => {
  if (data === true) {
    loading.value = true
    setTimeout(() => {
      pagination.value.pageNum = 1
      getTableData()
    }, 2000)
  }
}

onMounted(() => {
  communication.value = new WindowCommunication('lease_scheme_added', {
    timeout: 2000,
  })
  communication.value.on('lease_scheme_added', handleSchemeAdded)
})

// 在组件卸载时移除监听
onUnmounted(() => {
  if (communication.value) {
    communication.value.off('lease_scheme_added', handleSchemeAdded)
    communication.value.destroy()
  }
})

const handleDeleteSelected = (plan) => {
  // 从选中列表中移除
  const planIndex = selectedPlans.value.findIndex((item) => item.id === plan.id)
  if (planIndex !== -1) {
    selectedPlans.value.splice(planIndex, 1)
  }

  // 如果当前表格中存在该项，取消其选中状态
  const tableRow = tableData.value.find((item) => item.id === plan.id)
  if (tableRow) {
    multipleTableRef.value.$refs.table.toggleRowSelection(tableRow, false)
  }
}
</script>

<style lang="scss" scoped>
::v-deep .basic-list__table-header {
  > th.el-table__cell:first-child {
    > div.cell {
      display: none;
    }
  }
}
::v-deep {
  .el-alert__content {
    height: 22px;
  }
}
::v-deep .el-alert {
  padding: 7px 16px;
  margin-bottom: 12px;
}
.search-btn {
  margin-left: 12px;
}
.footer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  text-align: right;
}
.search-area {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  .search-form {
    .form-item {
      display: flex;
      align-items: center;

      .label {
        padding: 0 12px;
        height: 32px;
        line-height: 32px;
        border-radius: 6px 0 0 6px;
        background: #ffffff;
        border-width: 1px 0 1px 1px;
        border-style: solid;
        border-color: #e7e8eb;
        box-sizing: border-box;
        opacity: 1;
      }
    }
  }
  .add-plan {
    display: flex;
    align-items: center;
    font-size: 14px;

    .text {
      color: rgba(0, 0, 0, 0.6);
    }

    .action {
      padding: 0;
      margin-left: 4px;
      font-size: 14px;
      color: #366cfe;

      &:hover {
        color: #366cfe;
      }
    }
  }
}

::v-deep {
  .el-select {
    .el-input__inner {
      border-radius: 0 6px 6px 0;
      height: 32px;
      line-height: 32px;
      background: #ffffff;
      border: 1px solid #e7e8eb;
      box-sizing: border-box;
      opacity: 1;
    }
    .el-input__inner:hover,
    .el-input__inner:focus {
      border-color: #e7e8eb;
    }
  }
}

.selected-plans-list {
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px; // 添加右侧内边距，避免滚动条贴近内容

  // 自定义滚动条样式
  &::-webkit-scrollbar {
    width: 6px; // 减小滚动条宽度
  }

  &::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #dcdfe6;
    border-radius: 3px;

    &:hover {
      background: #c0c4cc; // 悬停时变深
    }
  }

  .selected-plan-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #ebeef5;

    &:last-child {
      border-bottom: none;
    }

    .plan-name {
      flex: 1;
      margin-right: 12px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .delete-btn {
      padding: 0;
      color: #f56c6c;

      &:hover {
        opacity: 0.8;
      }
    }
  }

  .empty-text {
    color: #909399;
    text-align: center;
    padding: 12px 0;
  }
}
::v-deep .selected-plans-popover {
  padding: 12px;
}
</style>
